import { AsyncValidatorFn, ValidatorFn, Validators } from '@angular/forms';
import {
  fileAspectRatioValidator,
  fileSizeValidator,
  fileTypeValidator,
  imageDimensionsAsyncValidator,
} from '@helpers/image-validators';
import { ImageUploadValidation } from '@models/image-upload-validation/1.0/image-upload-validation';

const PNG = 'image/png';
const JPG = 'image/jpeg';
const GIF = 'image/gif';

// UBEREATS
const uberEatsMimeTypes = [PNG, JPG].join(',');
const uberEatsValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(8000000),
  fileTypeValidator([PNG, JPG]),
]);

const uberEatsDimensions: [number, number, number, number] = [320, 1144, 320, 1144];
const uberEatsAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...uberEatsDimensions),
]);
const uberEatsInstructions = {
  fileSize: 'upload.ubereats.fileSize',
  fileType: 'upload.ubereats.fileType',
  imageDimensions: 'upload.ubereats.imageDimensions',
};

// POSTMATES
const postmatesMimeTypes = [PNG, JPG].join(',');
const postmatesValidator: ValidatorFn = Validators.compose([fileTypeValidator([PNG, JPG])]);
const postmatesDimensions: [number, number, number, number] = [710, 1080, 710, 1080];
const postmatesAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...postmatesDimensions),
]);
const postmatesInstructions = {
  fileType: 'upload.postmates.fileType',
  imageDimensions: 'upload.postmates.imageDimensions',
};

// DOORDASH
const doordashMimeTypes = [PNG, JPG].join(',');
const doordashValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(2000000),
  fileTypeValidator([PNG, JPG]),
]);
const doordashDimensions: [number, number, number, number] = [1400, 99999, 788, 99999];
const doordashAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...doordashDimensions),
]);
const doordashInstructions = {
  fileSize: 'upload.doordash.fileSize',
  fileType: 'upload.doordash.fileType',
  imageDimensions: 'upload.doordash.imageDimensions',
};

// FLYT
const flytMimeTypes = [PNG, JPG].join(',');
const flytValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(10000000),
  fileTypeValidator([PNG, JPG]),
]);
const flytDimensions: [number, number, number, number] = [1024, 99999, 768, 99999];
const flytAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...flytDimensions),
]);
const flytInstructions = {
  fileSize: 'upload.flyt.fileSize',
  fileType: 'upload.flyt.fileType',
  imageDimensions: 'upload.flyt.imageDimensions',
};

// HUGO
const hugoMimeTypes = [PNG, JPG].join(',');
const hugoValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(1000000),
  fileTypeValidator([PNG, JPG]),
]);
const hugoDimensions: [number, number, number, number] = [1024, 99999, 1024, 99999];
const hugoAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...hugoDimensions),
]);
const hugoInstructions = {
  fileSize: 'upload.hugo.fileSize',
  fileType: 'upload.hugo.fileType',
  imageDimensions: 'upload.hugo.imageDimensions',
};

// RAPPI
const rappiMimeTypes = [PNG, JPG].join(',');
const rappiValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(1000000),
  fileTypeValidator([PNG, JPG]),
]);
const rappiDimensions: [number, number, number, number] = [320, 99999, 320, 99999];
const rappiAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...rappiDimensions),
]);
const rappiInstructions = {
  fileSize: 'upload.rappi.fileSize',
  fileType: 'upload.rappi.fileType',
  imageDimensions: 'upload.rappi.imageDimensions',
};

//GRUBHUB
const grubhubMimeTypes = [JPG].join(',');
const grubhubValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(10000000),
  fileTypeValidator([JPG]),
]);
const grubhubDimensions: [number, number, number, number] = [1600, 99999, 1200, 99999];
const grubhubAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...grubhubDimensions),
]);
const grubhubInstructions = {
  fileSize: 'upload.grubhub.fileSize',
  fileType: 'upload.grubhub.fileType',
  imageDimensions: 'upload.grubhub.imageDimensions',
};

// JUSTEAT
const justeatMimeTypes = [JPG].join(',');
const justeatValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(10000000),
  fileTypeValidator([JPG]),
]);
const justeatDimensions: [number, number, number, number] = [320, 99999, 320, 99999];
const justeatAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...justeatDimensions),
]);
const justeatInstructions = {
  fileSize: 'upload.justeat.fileSize',
  fileType: 'upload.justeat.fileType',
  imageDimensions: 'upload.justeat.imageDimensions',
};

// DIDI
const didiMimeTypes = [PNG, JPG].join(',');
const didiValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(10000000),
  fileTypeValidator([PNG, JPG]),
]);
const didiDimensions: [number, number, number, number] = [150, 3000, 150, 3000];
const didiAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...didiDimensions),
]);
const didiInstructions = {
  fileSize: 'upload.didi.fileSize',
  fileType: 'upload.didi.fileType',
  imageDimensions: 'upload.didi.imageDimensions',
};

// BRAND LOGO, COUPON, PROGRAM
const logoMimeTypes = [PNG, JPG].join(',');
const logoValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(10000000),
  fileTypeValidator([PNG, JPG]),
]);
const logoDimensions: [number, number, number, number] = [1024, 99999, 768, 99999];
const logoAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...logoDimensions),
]);

export const logoInstructions = {
  fileSize: 'upload.logo.fileSize',
  fileType: 'upload.logo.fileType',
  imageDimensions: 'upload.logo.imageDimensions',
};

// DEFAULT IMAGES
const defaultImageMimeTypes = [PNG, JPG].join(',');
const defaultImageInstruction = {
  fileSize: 'upload.default-item.fileSize',
  fileType: 'upload.default-item.fileType',
  imageDimensions: 'upload.default-item.imageDimensions',
};
const defaultDimensions: [number, number, number, number] = [320, 99999, 320, 99999];
const defaultImageValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(10000000),
  fileTypeValidator([PNG, JPG]),
]);

const defaultImageAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...defaultDimensions),
]);

// STORE LARGE AND SMALL
const largeMimeTypes = [PNG, JPG].join(',');
const largeValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(10000000),
  fileTypeValidator([PNG, JPG]),
]);
const largeDimensions: [number, number, number, number] = [320, 1144, 320, 1144];
const largeAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...largeDimensions),
]);

const largeInstructions = {
  fileSize: 'upload.large.fileSize',
  fileType: 'upload.large.fileType',
  imageDimensions: 'upload.large.imageDimensions',
};

// CONSUMER API
const consumerApiMimeTypes = [PNG, JPG].join(',');
const consumerApiValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(10000000),
  fileTypeValidator([PNG, JPG]),
]);
const consumerApiDimensions: [number, number, number, number] = [120, 99999, 120, 99999];
const consumerApiAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...consumerApiDimensions),
]);

const consumerApiInstructions = {
  fileSize: 'upload.consumer-api.fileSize',
  fileType: 'upload.consumer-api.fileType',
  imageDimensions: 'upload.consumer-api.imageDimensions',
};

// PEDIDOSYA
const pedidosyaMimeTypes = [GIF, PNG, JPG].join(',');
const pedidosyaValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(20000000),
  fileTypeValidator([PNG, JPG, GIF]),
]);
const pedidosyaDimensions: [number, number, number, number] = [1440, 4000, 1080, 4000];
const pedidosyaAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...pedidosyaDimensions),
]);

const pedidosyaInstructions = {
  fileSize: 'upload.pedidosya.fileSize',
  fileType: 'upload.pedidosya.fileType',
  imageDimensions: 'upload.pedidosya.imageDimensions',
};

// SKIPTHEDISHES
const skipTheDishesAspectRatio = 3 / 2; // 1.5 (3:2 landscape)
const skipTheDishesAspectRatioTolerance = 0.01;
const skipTheDishesMimeTypes = [PNG, JPG].join(',');
const skipTheDishesValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(6000000),
  fileTypeValidator([PNG, JPG]),
]);
const skipTheDishesDimensions: [number, number, number, number] = [2100, 4000, 1400, 4000];
const skipTheDishesAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...skipTheDishesDimensions),
  fileAspectRatioValidator(skipTheDishesAspectRatio, skipTheDishesAspectRatioTolerance),
]);

const skipTheDishesInstructions = {
  fileSize: 'upload.skipthedishes.fileSize',
  fileType: 'upload.skipthedishes.fileType',
  imageDimensions: 'upload.skipthedishes.imageDimensions',
  imageAspectRatio: 'upload.skipthedishes.imageAspectRatio',
};

// DELIVEROO
const deliverooAspectRatio = 16 / 9; // 1.777... (16:9 landscape)
const deliverooAspectRatioTolerance = 0.01;
const deliverooMimeTypes = [PNG, JPG].join(',');
const deliverooValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(6000000),
  fileTypeValidator([PNG, JPG]),
]);
const deliverooDimensions: [number, number, number, number] = [1920, 1920, 1080, 1080];
const deliverooAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...deliverooDimensions),
  fileAspectRatioValidator(deliverooAspectRatio, deliverooAspectRatioTolerance),
]);

const deliverooInstructions = {
  fileSize: 'upload.deliveroo.fileSize',
  fileType: 'upload.deliveroo.fileType',
  imageDimensions: 'upload.deliveroo.imageDimensions',
  imageAspectRatio: 'upload.deliveroo.imageAspectRatio',
};

// BASE IMAGE
const baseImageMimeTypes = [GIF, PNG, JPG].join(',');
const baseImageValidator: ValidatorFn = Validators.compose([
  fileSizeValidator(20000000),
  fileTypeValidator([PNG, JPG, GIF]),
]);
const baseImageDimensions: [number, number, number, number] = [1, 99999, 1, 99999];
const baseImageAsyncValidator: AsyncValidatorFn = Validators.composeAsync([
  imageDimensionsAsyncValidator(...baseImageDimensions),
]);

const baseImageInstructions = {
  fileSize: 'upload.baseImage.fileSize',
};

export enum ImageUploadId {
  DOORDASH = 'doordash',
  UBEREATS = 'ubereats',
  UBEREATS_SPI = 'ubereats-spi',
  POSTMATES = 'postmates',
  GLOVO = 'glovo',
  FOODPANDA = 'food-panda',
  PEDIDOSYA = 'pedidosya',
  FLYT = 'flyt',
  HUGO = 'hugo',
  JUSTEAT = 'justeat',
  SKIPDISHES = 'skipthedishes',
  DELIVEROO = 'deliveroo',
  MENULOG = 'menulog',
  TAKEAWAY = 'takeaway',
  LOGO = 'logo',
  LARGE = 'large',
  SMALL = 'small',
  CONSUMER_API = 'consumer-api',
  WEB = 'web',
  MOBILE = 'mobile',
  CALL_CENTER = 'call_center',
  KIOSK = 'kiosk',
  INDOOR_DIGITAL_MENU_BOARD = 'indoor_digital_menu_board',
  OUTDOOR_DIGITAL_MENU_BOARD = 'outdoor_digital_menu_board',
  CHAT = 'chat',
  DEFAULT_PRODUCT = 'default-product',
  DEFAULT_COMBO = 'default-combo',
  DEFAULT_MODIFIER = 'default-modifier',
  DEFAULT_CATEGORY = 'default-category',
  COUPON = 'coupon',
  PROGRAM = 'program',
  REWARD = 'reward',
  BONUS = 'bonus',
  RAPPI = 'rappi',
  RAPPI_XL = 'rappi-xl',
  RAPPI_TURBO = 'rappi-turbo',
  GRUBHUB = 'grubhub',
  DIDI = 'didi',
  DIDI_XL = 'didi-xl',
  BASE_IMAGE = 'base-image',
}

export const nonChannelImageIds = [
  'logo',
  'large',
  'small',
  'default-product',
  'default-combo',
  'default-modifier',
  'default-category',
  'coupon',
  'program',
  'reward',
  'bonus',
  'base-image',
  'web',
  'mobile',
  'call_center',
  'kiosk',
  'indoor_digital_menu_board',
  'outdoor_digital_menu_board',
  'chat',
];

export const CXA_ORDERING_CHANNEL_IMAGE_IDS: ImageUploadId[] = [
  ImageUploadId.WEB,
  ImageUploadId.MOBILE,
  ImageUploadId.CALL_CENTER,
  ImageUploadId.KIOSK,
  ImageUploadId.INDOOR_DIGITAL_MENU_BOARD,
  ImageUploadId.OUTDOOR_DIGITAL_MENU_BOARD,
  ImageUploadId.CHAT,
];

export class ImageUploadValidationFactory {
  static get(aggregatorId: ImageUploadId): ImageUploadValidation {
    switch (aggregatorId) {
      case ImageUploadId.UBEREATS:
      case ImageUploadId.UBEREATS_SPI:
      case ImageUploadId.GLOVO:
      case ImageUploadId.FOODPANDA:
        return new ImageUploadValidation(
          uberEatsMimeTypes,
          uberEatsValidator,
          uberEatsAsyncValidator,
          uberEatsInstructions,
          uberEatsDimensions,
        );

      case ImageUploadId.POSTMATES:
        return new ImageUploadValidation(
          postmatesMimeTypes,
          postmatesValidator,
          postmatesAsyncValidator,
          postmatesInstructions,
          postmatesDimensions,
        );

      case ImageUploadId.DOORDASH:
        return new ImageUploadValidation(
          doordashMimeTypes,
          doordashValidator,
          doordashAsyncValidator,
          doordashInstructions,
          doordashDimensions,
        );
      case ImageUploadId.HUGO:
        return new ImageUploadValidation(
          hugoMimeTypes,
          hugoValidator,
          hugoAsyncValidator,
          hugoInstructions,
          hugoDimensions,
        );

      case ImageUploadId.FLYT:
      case ImageUploadId.MENULOG:
      case ImageUploadId.TAKEAWAY:
        return new ImageUploadValidation(
          flytMimeTypes,
          flytValidator,
          flytAsyncValidator,
          flytInstructions,
          flytDimensions,
        );

      case ImageUploadId.SKIPDISHES:
        return new ImageUploadValidation(
          skipTheDishesMimeTypes,
          skipTheDishesValidator,
          skipTheDishesAsyncValidator,
          skipTheDishesInstructions,
          skipTheDishesDimensions,
        );

      case ImageUploadId.DELIVEROO:
        return new ImageUploadValidation(
          deliverooMimeTypes,
          deliverooValidator,
          deliverooAsyncValidator,
          deliverooInstructions,
          deliverooDimensions,
        );

      case ImageUploadId.RAPPI:
      case ImageUploadId.RAPPI_XL:
      case ImageUploadId.RAPPI_TURBO:
        return new ImageUploadValidation(
          rappiMimeTypes,
          rappiValidator,
          rappiAsyncValidator,
          rappiInstructions,
          rappiDimensions,
        );

      case ImageUploadId.GRUBHUB:
        return new ImageUploadValidation(
          grubhubMimeTypes,
          grubhubValidator,
          grubhubAsyncValidator,
          grubhubInstructions,
          grubhubDimensions,
        );

      case ImageUploadId.JUSTEAT:
        return new ImageUploadValidation(
          justeatMimeTypes,
          justeatValidator,
          justeatAsyncValidator,
          justeatInstructions,
          justeatDimensions,
        );

      case ImageUploadId.DIDI:
      case ImageUploadId.DIDI_XL:
        return new ImageUploadValidation(
          didiMimeTypes,
          didiValidator,
          didiAsyncValidator,
          didiInstructions,
          didiDimensions,
        );

      case ImageUploadId.LOGO:
      case ImageUploadId.COUPON:
      case ImageUploadId.PROGRAM:
      case ImageUploadId.REWARD:
      case ImageUploadId.BONUS:
        return new ImageUploadValidation(
          logoMimeTypes,
          logoValidator,
          logoAsyncValidator,
          logoInstructions,
          logoDimensions,
        );
      case ImageUploadId.DEFAULT_CATEGORY:
      case ImageUploadId.DEFAULT_COMBO:
      case ImageUploadId.DEFAULT_MODIFIER:
      case ImageUploadId.DEFAULT_PRODUCT:
        return new ImageUploadValidation(
          defaultImageMimeTypes,
          defaultImageValidator,
          defaultImageAsyncValidator,
          defaultImageInstruction,
          defaultDimensions,
        );
      case ImageUploadId.LARGE:
      case ImageUploadId.SMALL:
        return new ImageUploadValidation(
          largeMimeTypes,
          largeValidator,
          largeAsyncValidator,
          largeInstructions,
          largeDimensions,
        );
      case ImageUploadId.CONSUMER_API:
      case ImageUploadId.WEB:
      case ImageUploadId.MOBILE:
      case ImageUploadId.CALL_CENTER:
      case ImageUploadId.KIOSK:
      case ImageUploadId.INDOOR_DIGITAL_MENU_BOARD:
      case ImageUploadId.OUTDOOR_DIGITAL_MENU_BOARD:
      case ImageUploadId.CHAT:
        return new ImageUploadValidation(
          consumerApiMimeTypes,
          consumerApiValidator,
          consumerApiAsyncValidator,
          consumerApiInstructions,
          consumerApiDimensions,
        );
      case ImageUploadId.PEDIDOSYA:
        return new ImageUploadValidation(
          pedidosyaMimeTypes,
          pedidosyaValidator,
          pedidosyaAsyncValidator,
          pedidosyaInstructions,
          pedidosyaDimensions,
        );
      case ImageUploadId.BASE_IMAGE:
        return new ImageUploadValidation(
          baseImageMimeTypes,
          baseImageValidator,
          baseImageAsyncValidator,
          baseImageInstructions,
          baseImageDimensions,
        );
      default:
        return undefined;
    }
  }
}
